import { useMemo } from 'react'
import { LinkOff, Receipt, ShoppingCart } from '@mui/icons-material'
import {
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { OverlayState } from '@/hooks/useOverlay'
import { useDeletePOLineItemMatch } from '@/service-library/hooks/po-matches'
import { useGetPDPurchaseOrders } from '@/services/hooks/relay/dynamic'
import { DocumentRowValue } from '@/types/documents'
import { PDPurchaseOrderLineItem, POLineItemMatch } from '@/types/po-match'
import {
  INVOICE_LINE_ITEM_AMOUNT_INFO_TYPE_ID,
  INVOICE_LINE_ITEM_DESCRIPTION_INFO_TYPE_ID,
  LINE_ITEM_JOB_INFO_TYPE_ID,
  LINE_ITEM_PHASE_INFO_TYPE_ID,
  LINE_ITEM_QUANTITY_SHIPPED_INFO_TYPE_ID,
  LINE_ITEM_RATE_INFO_TYPE_ID,
  PURCHASE_ORDER_INFO_TYPE_ID,
} from '@/utils/info-types'
import { showErrorSnackbar } from '@/utils/snackbars'
import { Dialog, DialogContent } from '@/components/dialog'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { formatValue } from '@/components/purchase-order-view/shared'

type POLineItemMatchesDialogProps = {
  overlay: OverlayState
  onExited: () => void
  documentRowId: string
  poLineItemMatches: POLineItemMatch[]
  documentRowValues: DocumentRowValue[]
  listQueryKey: QueryKey
}

const INFO_TYPE_FIELDS: {
  key: string
  label: string
  infoTypeId: string
}[] = [
  {
    key: 'description',
    label: 'Description',
    infoTypeId: INVOICE_LINE_ITEM_DESCRIPTION_INFO_TYPE_ID,
  },
  { key: 'job', label: 'Job', infoTypeId: LINE_ITEM_JOB_INFO_TYPE_ID },
  { key: 'phase', label: 'Phase', infoTypeId: LINE_ITEM_PHASE_INFO_TYPE_ID },
  {
    key: 'quantity',
    label: 'Quantity',
    infoTypeId: LINE_ITEM_QUANTITY_SHIPPED_INFO_TYPE_ID,
  },
  { key: 'rate', label: 'Rate', infoTypeId: LINE_ITEM_RATE_INFO_TYPE_ID },
  {
    key: 'amount',
    label: 'Amount',
    infoTypeId: INVOICE_LINE_ITEM_AMOUNT_INFO_TYPE_ID,
  },
]

const COLUMNS = ['Description', 'Job', 'Phase', 'Quantity', 'Rate', 'Amount']

const headerSx = {
  py: 0.5,
  px: 1.5,
  borderBottom: 'none',
}

const cellSx = {
  py: 1,
  px: 1.5,
  borderBottom: 'none',
}

export default function POLineItemMatchesDialog({
  overlay,
  onExited,
  documentRowId,
  poLineItemMatches,
  documentRowValues,
  listQueryKey,
}: POLineItemMatchesDialogProps) {
  const { document } = useDocumentContext()
  const { project, fieldsMap } = useProjectContext()

  const matchesForRow = useMemo(
    () => poLineItemMatches.filter((m) => m.document_row_id === documentRowId),
    [poLineItemMatches, documentRowId],
  )

  const poIds = useMemo(
    () => [...new Set(matchesForRow.map((m) => m.po_id))],
    [matchesForRow],
  )

  const { pdPurchaseOrders, isFetching: isFetchingPOs } =
    useGetPDPurchaseOrders({
      infoTypeId: PURCHASE_ORDER_INFO_TYPE_ID,
      orgId: document?.owner_org_id || '',
      orgCredentialTypeId: project?.org_credential_type_id || '',
      filters: { ids: poIds.join(',') },
      enabled: poIds.length > 0,
      placeholderData: (previousData) => previousData,
    })

  const poLineItemMap = useMemo(() => {
    const map = new Map<string, PDPurchaseOrderLineItem>()
    pdPurchaseOrders.forEach((po) => {
      po.line_items.forEach((li) => {
        map.set(`${po.external_id}::${li.external_id}`, li)
      })
    })
    return map
  }, [pdPurchaseOrders])

  const poNumberMap = useMemo(() => {
    const map = new Map<string, string>()
    pdPurchaseOrders.forEach((po) => {
      map.set(po.external_id, po.number)
    })
    return map
  }, [pdPurchaseOrders])

  const invoiceValuesByName = useMemo(() => {
    const map: Record<string, { fieldName: string; value: string | null }> = {}
    const rowValues = documentRowValues.filter(
      (rv) => rv.document_row_id === documentRowId,
    )

    for (const { key, label, infoTypeId } of INFO_TYPE_FIELDS) {
      const rv = rowValues.find((rowValue) => {
        const field = fieldsMap[rowValue.project_grid_field_id]
        return field?.info_type_id === infoTypeId
      })
      if (rv) {
        map[key] = { fieldName: label, value: rv.final_value }
      }
    }

    return map
  }, [documentRowValues, documentRowId, fieldsMap])

  const { deletePOLineItemMatch } = useDeletePOLineItemMatch({
    listQueryKey,
    onError: () => {
      showErrorSnackbar('Failed to delete match. Please try again.')
    },
  })

  return (
    <Dialog
      {...overlay}
      title="PO Line Item Matches"
      maxWidth="lg"
      TransitionProps={{ onExited }}
    >
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          This line item has been matched with one or more Purchase Order line
          items. Changes to the line item&apos;s values will not automatically
          remove matches.
        </Typography>

        <Stack
          direction="row"
          spacing={3}
          sx={{ alignItems: 'flex-start', pb: 0.5 }}
        >
          {/* Left: Invoice line item */}
          <Paper
            variant="outlined"
            sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing="1"
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                minHeight: 44,
              }}
            >
              <Receipt fontSize="small" />
              <Typography variant="subtitle2" fontWeight={600}>
                Line Item
              </Typography>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {COLUMNS.map((col) => (
                    <TableCell key={col} sx={headerSx}>
                      <Typography variant="caption" color="text.secondary">
                        {col}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {COLUMNS.map((col) => {
                    const entry = invoiceValuesByName[col.toLowerCase()]
                    return (
                      <TableCell key={col} sx={cellSx}>
                        <Typography variant="body2" fontWeight={500}>
                          {entry?.value || '—'}
                        </Typography>
                      </TableCell>
                    )
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </Paper>

          {/* Right: Matched PO line items */}
          <Paper
            variant="outlined"
            sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: 'success.main',
                color: 'success.contrastText',
                minHeight: 44,
              }}
            >
              <ShoppingCart fontSize="small" />
              <Typography variant="subtitle2" fontWeight={600}>
                Purchase Order
              </Typography>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerSx}>
                    <Typography variant="caption" color="text.secondary">
                      PO#
                    </Typography>
                  </TableCell>
                  {COLUMNS.map((col) => (
                    <TableCell key={col} sx={headerSx}>
                      <Typography variant="caption" color="text.secondary">
                        {col}
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell sx={{ ...headerSx, width: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {matchesForRow.map((match) => {
                  const li = poLineItemMap.get(
                    `${match.po_id}::${match.po_line_item_id}`,
                  )
                  return (
                    <TableRow key={match.id}>
                      <TableCell sx={cellSx}>
                        {isFetchingPOs ? (
                          <Skeleton width={80} />
                        ) : (
                          <Chip
                            label={poNumberMap.get(match.po_id) ?? match.po_id}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {isFetchingPOs ? (
                          <Skeleton width={120} />
                        ) : (
                          <Typography variant="body2" fontWeight={500}>
                            {li?.description || '—'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Typography variant="body2" fontWeight={500}>
                          —
                        </Typography>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Typography variant="body2" fontWeight={500}>
                          —
                        </Typography>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {isFetchingPOs ? (
                          <Skeleton width={50} />
                        ) : (
                          <Typography variant="body2" fontWeight={500}>
                            {formatValue(li?.quantity ?? null)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {isFetchingPOs ? (
                          <Skeleton width={50} />
                        ) : (
                          <Typography variant="body2" fontWeight={500}>
                            {formatValue(li?.unit_price ?? null)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {isFetchingPOs ? (
                          <Skeleton width={50} />
                        ) : (
                          <Typography variant="body2" fontWeight={500}>
                            {formatValue(li?.amount ?? null)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, p: 0 }}>
                        <Tooltip title="Remove match">
                          <IconButton
                            size="small"
                            onClick={() => deletePOLineItemMatch(match.id)}
                          >
                            <LinkOff fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Paper>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
